import { m } from "framer-motion";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { EASE } from "../Motion";
import { BoltIcon, ChevronLeft, Face, FlagIcon, GhostIcon, SpeakerIcon, Stars, StopwatchIcon, TrophyIcon, formatTime, rgbCss } from "./KartUi";
import { kartStyle } from "./renderer";
import { Assists, DIFFICULTIES, Difficulty, Racer, starsFor } from "./sim";
import { MAPS, Track, VARIANTS, Variant, buildTrack, trackId, variantOf } from "./tracks";

// The kart game's menu. The demo lap runs behind it, so the menu is built
// like a title sequence over live footage: a big wordmark at the top, the
// middle of the screen left clear for the race, and one frosted sheet at the
// bottom with everything to press. Picking a map is a carousel of poster
// cards, one per track, each with the records to beat — the fastest time and
// the most points, and whose they are. Help lives in settings, off unless you
// turn it on, and a race driven with any help doesn't count.

export type MenuPage = "home" | "maps" | "settings";
/** A race against the field, or training: you alone on the road, nothing counted. */
export type Mode = "race" | "training";

/** A member's best on a track and difficulty, named. */
export interface MenuRecord {
  memberId: number;
  track: string;
  difficulty: string;
  score: number;
  time: number | null;
  name: string;
  emoji: string;
}

export const ASSIST_LABELS = {
  steer: { label: "Styrehjælp", text: "Skubber dig væk fra væggen" },
  drift: { label: "Let drift", text: "Lettere at starte, kun op til første trin" },
  item: { label: "Automatiske skud", text: "Bilen bruger dine ting for dig" },
} as const;

export const STEER_LABELS = { high: "Høj", normal: "Normal", low: "Lav" } as const;

interface Props {
  me: Racer;
  page: MenuPage;
  setPage: (p: MenuPage) => void;
  mode: Mode;
  setMode: (m: Mode) => void;
  mapKey: string;
  setMapKey: (k: string) => void;
  variant: Variant;
  setVariant: (v: Variant) => void;
  difficulty: Difficulty;
  setDifficulty: (d: Difficulty) => void;
  records: MenuRecord[];
  /** Your best score per `track:difficulty`, kept on this phone. */
  best: Record<string, number>;
  assists: Assists;
  toggleAssist: (key: keyof Assists) => void;
  sensitivity: keyof typeof STEER_LABELS;
  setSensitivity: (s: keyof typeof STEER_LABELS) => void;
  challenge: { mapKey: string; variant: Variant; difficulty: Difficulty; goal: "score" | "time" };
  week: { memberId: number; name: string; emoji: string; score: number; time: number | null }[];
  mute: boolean;
  toggleMute: () => void;
  onStart: () => void;
  onExit?: () => void;
  notice?: string;
}

const helped = (a: Assists) => a.steer || a.drift || a.item;

/** Each track's poster: its colours, dark to light, and the one its line glows in. */
const ART: Record<string, { from: string; to: string; glow: string; tag: string }> = {
  forest: { from: "#06140d", to: "#1f5a37", glow: "#8ef0a0", tag: "Skov · Grus" },
  meadow: { from: "#051a2b", to: "#1b6d6a", glow: "#7fe3ff", tag: "Sø · Eng" },
  snow: { from: "#0a1430", to: "#4d6fa3", glow: "#d8ecff", tag: "Alperne · Is" },
};
const artOf = (key: string) => ART[key] ?? ART.forest;

/** The gold of the whole menu: warm yellow into orange, like the flames off a boost. */
const GOLD = "linear-gradient(135deg, #ffe066 0%, #ffc233 40%, #ff8a1f 100%)";

const GLASS = "bg-[#0c0f14]/60 backdrop-blur-2xl border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]";

export default function KartMenu(p: Props) {
  return (
    <m.div
      key="menu"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: EASE }}
      className="absolute inset-0 flex flex-col text-white"
    >
      {p.page === "home" && <Home key="home" {...p} />}
      {p.page === "maps" && <Maps key="maps" {...p} />}
      {p.page === "settings" && <Settings key="settings" {...p} />}
    </m.div>
  );
}

const enter = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: EASE },
};
const stagger = (i: number) => ({ ...enter, transition: { ...enter.transition, delay: 0.08 + i * 0.06 } });

// ── Pieces ───────────────────────────────────────────────────────────────────

/** The one button that starts things: gold, heavy, with a glint running across it now and then. */
function Primary({ label, sub, onClick }: { label: string; sub?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative w-full h-[72px] rounded-[22px] overflow-hidden text-[#1a1206] active:scale-[0.98] transition-transform duration-150"
      style={{ background: GOLD, boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset, 0 -3px 0 rgba(120,50,0,0.35) inset, 0 16px 40px -12px rgba(255,150,30,0.7)" }}
    >
      <m.span
        aria-hidden
        className="absolute inset-y-0 w-1/3 -skew-x-12"
        style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)" }}
        initial={{ left: "-40%" }}
        animate={{ left: "140%" }}
        transition={{ duration: 1.1, ease: "easeInOut", repeat: Infinity, repeatDelay: 2.6 }}
      />
      <span className="relative flex items-center h-full pl-3 pr-5 gap-3.5">
        <span className="w-12 h-12 rounded-2xl bg-[#1a1206] flex items-center justify-center shadow-inner">
          <FlagIcon size={26} />
        </span>
        <span className="flex flex-col items-start leading-none">
          <span className="font-display font-extrabold italic uppercase text-[25px] tracking-[-0.02em]">{label}</span>
          {sub && <span className="mt-1 font-mono text-[9.5px] tracking-[0.18em] uppercase opacity-70">{sub}</span>}
        </span>
        <ArrowRight className="ml-auto transition-transform group-active:translate-x-1" />
      </span>
    </button>
  );
}

const ArrowRight = ({ className = "" }: { className?: string }) => (
  <svg aria-hidden viewBox="0 0 24 24" width={24} height={24} className={className}>
    <path d="M4 12h15M13 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GearIcon = ({ size = 22 }: { size?: number }) => (
  <svg aria-hidden viewBox="0 0 24 24" width={size} height={size}>
    <path
      d="M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Zm8.3 5.1-1.8-.3a6.9 6.9 0 0 1-.6 1.5l1 1.5-2 2-1.5-1a6.9 6.9 0 0 1-1.5.6l-.3 1.8h-2.8l-.3-1.8a6.9 6.9 0 0 1-1.5-.6l-1.5 1-2-2 1-1.5a6.9 6.9 0 0 1-.6-1.5l-1.8-.3v-2.8l1.8-.3c.1-.5.3-1 .6-1.5l-1-1.5 2-2 1.5 1c.5-.3 1-.5 1.5-.6l.3-1.8h2.8l.3 1.8c.5.1 1 .3 1.5.6l1.5-1 2 2-1 1.5c.3.5.5 1 .6 1.5l1.8.3Z"
      fill="currentColor"
    />
  </svg>
);

function Tile({ label, icon, tint, onClick }: { label: string; icon: ReactNode; tint: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex flex-col items-start gap-2.5 rounded-[20px] bg-white/[0.06] border border-white/10 p-3 text-left active:scale-[0.97] active:bg-white/10 transition"
    >
      <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: tint }}>
        {icon}
      </span>
      <span className="w-full min-w-0 leading-tight">
        <span className="block font-display text-[15px] font-bold uppercase tracking-tight">{label}</span>
      </span>
    </button>
  );
}

function Header({ over, title, onBack, right }: { over: string; title: string; onBack: () => void; right?: ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-4 pt-4 pb-2">
      <button
        type="button"
        aria-label="Tilbage"
        onClick={onBack}
        className={`w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition ${GLASS}`}
      >
        <ChevronLeft />
      </button>
      <div className="leading-none">
        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-[#ffc233]">{over}</div>
        <h2 className="mt-1 font-display font-extrabold italic uppercase text-[28px] tracking-[-0.03em]">{title}</h2>
      </div>
      <div className="ml-auto">{right}</div>
    </div>
  );
}

/** Whether this run counts, in one line: green, amber, or training blue. */
function Counts({ mode, assists, small }: { mode: Mode; assists: Assists; small?: boolean }) {
  // Nothing to say about a clean race: only what stops one counting.
  if (mode !== "training" && !helped(assists)) return null;
  const [tone, text] =
    mode === "training" ? ["#8ec5ff", "Træning · tid og point tæller ikke"] : ["#ffc233", "Med hjælp · tæller ikke på ranglisten"];
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.14em] ${small ? "text-[8.5px]" : "text-[9.5px]"}`} style={{ color: tone }}>
      <span className="relative flex w-1.5 h-1.5">
        <span className="absolute inset-0 rounded-full animate-ping opacity-60 motion-reduce:animate-none" style={{ background: tone }} />
        <span className="relative w-1.5 h-1.5 rounded-full" style={{ background: tone }} />
      </span>
      {text}
    </span>
  );
}

// ── Home ─────────────────────────────────────────────────────────────────────

function Home(p: Props) {
  const style = kartStyle(p.me);
  const leader = p.week[0];
  const weekMap = MAPS.find((m) => m.key === p.challenge.mapKey)!;
  return (
    <div className="relative flex-1 flex flex-col min-h-0">
      {/* Shade over the footage: dark at the top for the title, darker at the foot for the sheet. */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[46%] bg-gradient-to-b from-black/75 via-black/30 to-transparent pointer-events-none" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-black/90 via-black/55 to-transparent pointer-events-none" />

      <m.header {...stagger(0)} className="relative px-6 pt-[max(56px,8vh)] select-none">
        <div className="flex items-center gap-2.5 font-mono text-[9.5px] tracking-[0.34em] uppercase text-white/70">
          <span className="w-6 h-px bg-[#ffc233]" />
          Logens Grand Prix
        </div>
        <div className="relative mt-2 -ml-1 -rotate-[4deg] origin-left">
          <div
            className="font-display font-extrabold italic uppercase text-[26px] leading-none tracking-[0.42em] text-transparent"
            style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.85)" }}
          >
            DSS
          </div>
          <div
            className="font-display font-extrabold italic uppercase text-[104px] leading-[0.82] tracking-[-0.05em] bg-clip-text text-transparent"
            style={{
              backgroundImage: GOLD,
              filter: "drop-shadow(0 2px 0 #b34d00) drop-shadow(0 4px 0 #6e2c00) drop-shadow(0 14px 22px rgba(0,0,0,0.55))",
            }}
          >
            Kart
          </div>
          {/* A chequered flag stripe, whipping in under the name. */}
          <m.div
            aria-hidden
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.25 }}
            className="mt-3 h-[10px] w-[74%] origin-left -skew-x-[20deg] rounded-[2px]"
            style={{ backgroundImage: "repeating-conic-gradient(#fff 0% 25%, #0b0b0b 0% 50%)", backgroundSize: "10px 10px" }}
          />
          <m.div
            aria-hidden
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.35 }}
            className="mt-1.5 h-[3px] w-[52%] origin-left -skew-x-[20deg] bg-[#ff3b30]"
          />
        </div>
      </m.header>

      <div className="flex-1" />

      <m.section {...stagger(2)} className={`relative mx-3 mb-3 rounded-[30px] p-3.5 flex flex-col gap-3 ${GLASS}`}>
        {/* Who you are, and whether what you drive now counts. */}
        <div className="flex items-center gap-3 px-1">
          <span
            className="relative w-[58px] h-[58px] rounded-full p-[2.5px] flex-shrink-0"
            style={{ background: `conic-gradient(from 200deg, ${rgbCss(style.body)}, ${rgbCss(style.accent)}, ${rgbCss(style.body)})` }}
          >
            <span className="block w-full h-full rounded-full overflow-hidden bg-[#0c0f14] text-[22px]">
              <Face racer={p.me} />
            </span>
          </span>
          <div className="min-w-0 leading-none">
            <div className="font-mono text-[8.5px] tracking-[0.24em] uppercase text-white/55">Du kører som</div>
            <div className="mt-1 font-display text-[24px] font-extrabold italic uppercase tracking-[-0.02em] truncate">{p.me.name}</div>
            {helped(p.assists) && (
              <div className="mt-1.5">
                <Counts mode="race" assists={p.assists} small />
              </div>
            )}
          </div>
        </div>
        {/* This week's challenge: a ticket to tap. */}
        <button
          type="button"
          onClick={() => {
            p.setMapKey(p.challenge.mapKey);
            p.setVariant(p.challenge.variant);
            p.setDifficulty(p.challenge.difficulty);
            p.setMode("race");
            p.setPage("maps");
          }}
          className="relative flex items-center gap-3 rounded-[20px] px-3 py-2.5 text-left overflow-hidden active:scale-[0.98] transition"
          style={{ background: "linear-gradient(100deg, rgba(255,194,51,0.18), rgba(255,138,31,0.06) 60%, rgba(255,255,255,0.03))", boxShadow: "inset 0 0 0 1px rgba(255,194,51,0.35)" }}
        >
          <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#ffc233]/15">
            <TrophyIcon size={22} />
          </span>
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block font-mono text-[8.5px] tracking-[0.24em] uppercase text-[#ffc233]">Ugens udfordring</span>
            <span className="block font-display text-[15px] font-bold uppercase tracking-tight truncate">
              {weekMap.name}
              {p.challenge.variant !== "normal" && ` · ${VARIANTS[p.challenge.variant]}`} · {p.challenge.goal === "score" ? "flest point" : "hurtigste tid"}
            </span>
            {leader && (
              <span className="block font-mono text-[9px] text-white/60 truncate">
                Fører {leader.name} · {p.challenge.goal === "score" ? leader.score.toLocaleString("da-DK") : formatTime(leader.time ?? 0)}
              </span>
            )}
          </span>
          <ArrowRight className="text-[#ffc233] w-5 h-5 flex-shrink-0" />
        </button>

        <Primary
          label="Kør løb"
          sub="Mod de 6 andre"
          onClick={() => {
            p.setMode("race");
            p.setPage("maps");
          }}
        />
        <div className="grid grid-cols-2 gap-2.5">
          <Tile
            label="Træning"
            tint="linear-gradient(135deg, #3b82f6, #1e40af)"
            icon={<GhostIcon size={22} />}
            onClick={() => {
              p.setMode("training");
              p.setPage("maps");
            }}
          />
          <Tile label="Indstillinger" tint="linear-gradient(135deg, #4b5563, #1f2937)" icon={<GearIcon />} onClick={() => p.setPage("settings")} />
        </div>
        {p.notice && <div className="px-1 font-mono text-[8.5px] leading-snug text-white/50 break-words">{p.notice}</div>}
      </m.section>
    </div>
  );
}

// ── Maps ─────────────────────────────────────────────────────────────────────

function Maps(p: Props) {
  const training = p.mode === "training";
  const index = Math.max(0, MAPS.findIndex((m) => m.key === p.mapKey));
  const chosen = MAPS[index];
  const rail = useRef<HTMLDivElement>(null);
  const settle = useRef(0);

  /** How far apart the cards sit, scrolled. */
  const pitch = () => {
    const el = rail.current;
    const first = el?.children[0] as HTMLElement | undefined;
    const second = el?.children[1] as HTMLElement | undefined;
    return first && second ? second.offsetLeft - first.offsetLeft : el?.clientWidth ?? 1;
  };
  const scrollTo = (i: number, smooth = true) => rail.current?.scrollTo({ left: i * pitch(), behavior: smooth ? "smooth" : "auto" });
  useEffect(() => {
    scrollTo(index, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The card that comes to rest in the middle is the one picked.
  const onScroll = () => {
    cancelAnimationFrame(settle.current);
    settle.current = requestAnimationFrame(() => {
      const el = rail.current;
      if (!el) return;
      const i = Math.min(MAPS.length - 1, Math.max(0, Math.round(el.scrollLeft / pitch())));
      if (MAPS[i].key !== p.mapKey) p.setMapKey(MAPS[i].key);
    });
  };

  const art = artOf(chosen.key);
  return (
    <m.div {...enter} className="relative flex-1 flex flex-col min-h-0">
      {/* The chosen track's colours wash over the whole page. */}
      <div aria-hidden className="absolute inset-0 transition-[background] duration-500" style={{ background: `radial-gradient(120% 70% at 50% 30%, ${art.to}cc, ${art.from}f2 60%, #05070a)` }} />
      <div className="relative flex flex-col flex-1 min-h-0">
        <Header over={training ? "Træning" : "Grand Prix"} title="Vælg bane" onBack={() => p.setPage("home")} />

        <div
          ref={rail}
          onScroll={onScroll}
          className="flex-1 min-h-0 flex gap-3 overflow-x-auto snap-x snap-mandatory px-[9vw] py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {MAPS.map((map, i) => (
            <MapCard key={map.key} n={i} map={map} selected={i === index} onPick={() => scrollTo(i)} {...p} />
          ))}
        </div>

        <div className="flex justify-center gap-1.5 pb-2" aria-hidden>
          {MAPS.map((map, i) => (
            <span key={map.key} className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-white" : "w-1.5 bg-white/30"}`} />
          ))}
        </div>

        <div className={`mx-3 mb-3 rounded-[30px] p-3.5 flex flex-col gap-3 ${GLASS}`}>
          <Segmented
            label="Udgave"
            id="variant"
            options={(Object.keys(VARIANTS) as Variant[]).map((v) => ({ key: v, label: VARIANTS[v] }))}
            value={p.variant}
            onChange={(v) => p.setVariant(v as Variant)}
          />
          {!training && (
            <Segmented
              label="Sværhed"
              id="difficulty"
              options={(Object.keys(DIFFICULTIES) as Difficulty[]).map((d) => ({ key: d, label: DIFFICULTIES[d] }))}
              value={p.difficulty}
              onChange={(d) => p.setDifficulty(d as Difficulty)}
            />
          )}
          {(training || helped(p.assists)) && (
            <div className="flex justify-center">
              <Counts mode={p.mode} assists={p.assists} />
            </div>
          )}
          <Primary label={chosen.name} sub={training ? "Start træning" : "Start løbet"} onClick={p.onStart} />
        </div>
      </div>
    </m.div>
  );
}

function MapCard({ map, n, selected, onPick, ...p }: Props & { map: (typeof MAPS)[number]; n: number; selected: boolean; onPick: () => void }) {
  const it = useMemo(() => variantOf(map, p.variant), [map, p.variant]);
  const track = useMemo(() => buildTrack(it), [it]);
  const art = artOf(map.key);
  const id = trackId(it);
  // The lodge's records, with your own best on this phone folded in: a run counts the moment it's driven, before the lodge's numbers are back.
  const saved = p.records.find((r) => r.memberId === p.me.id && r.track === id && r.difficulty === p.difficulty);
  const localScore = p.best[`${id}:${p.difficulty}`];
  const localTime = p.best[`${id}:${p.difficulty}:t`];
  const mine: MenuRecord | undefined =
    saved || localScore !== undefined || localTime !== undefined
      ? {
          memberId: p.me.id,
          track: id,
          difficulty: p.difficulty,
          name: p.me.name,
          emoji: p.me.emoji,
          score: Math.max(saved?.score ?? -1, localScore ?? -1),
          time: Math.min(saved?.time ?? Infinity, localTime ?? Infinity) === Infinity ? null : Math.min(saved?.time ?? Infinity, localTime ?? Infinity),
        }
      : undefined;
  const rows = [...p.records.filter((r) => r.track === id && r.difficulty === p.difficulty && r.memberId !== p.me.id), ...(mine ? [mine] : [])];
  const fastest = rows.filter((r) => r.time !== null).sort((a, b) => a.time! - b.time!)[0];
  // Points count across the difficulties — they're worth more on a harder one, so the record's held on the hardest — while times stay per difficulty.
  const diffs = Object.keys(DIFFICULTIES) as Difficulty[];
  const myScore = Math.max(-1, ...p.records.filter((r) => r.track === id && r.memberId === p.me.id).map((r) => r.score), ...diffs.map((d) => p.best[`${id}:${d}`] ?? -1));
  const myTop = p.records.filter((r) => r.track === id && r.memberId === p.me.id && r.score === myScore)[0];
  const scoreRows: MenuRecord[] = [
    ...p.records.filter((r) => r.track === id && r.memberId !== p.me.id),
    ...(myScore >= 0
      ? [{ memberId: p.me.id, track: id, difficulty: myTop?.difficulty ?? diffs.find((d) => p.best[`${id}:${d}`] === myScore) ?? p.difficulty, name: p.me.name, emoji: p.me.emoji, score: myScore, time: null }]
      : []),
  ];
  const top = scoreRows.slice().sort((a, b) => b.score - a.score)[0];
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={map.name}
      onClick={onPick}
      className={`relative snap-center flex-none w-[82vw] max-w-[360px] rounded-[30px] overflow-hidden text-left flex flex-col transition-[transform,opacity] duration-300 ${
        selected ? "scale-100 opacity-100" : "scale-[0.92] opacity-55"
      }`}
      style={{
        background: `linear-gradient(160deg, ${art.to} 0%, ${art.from} 72%)`,
        boxShadow: selected ? `0 0 0 1.5px ${art.glow}66, 0 30px 60px -24px ${art.glow}55` : "0 0 0 1px rgba(255,255,255,0.08)",
      }}
    >
      {/* A fine diagonal texture, like a printed poster. */}
      <span aria-hidden className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 7px)" }} />
      <div className="relative flex items-start justify-between px-5 pt-4">
        <div className="leading-none">
          <div className="font-mono text-[9px] tracking-[0.28em] uppercase" style={{ color: art.glow }}>
            {String(n + 1).padStart(2, "0")} — {art.tag}
          </div>
          <div className="mt-2 font-display text-[36px] font-extrabold italic uppercase tracking-[-0.04em] leading-[0.9] drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
            {map.name}
          </div>
        </div>
        {myScore >= 0 && (
          <span className="mt-0.5 rounded-full bg-black/30 px-2 py-1">
            <Stars count={starsFor(myScore, it.stars)} size={11} />
          </span>
        )}
      </div>

      <div className="relative flex-1 min-h-[110px] px-6 py-2">
        <TrackLine track={track} glow={art.glow} />
      </div>

      <div className="relative grid grid-cols-2 gap-px bg-white/10 border-t border-white/10">
        <Record icon={<StopwatchIcon size={12} />} label="Hurtigste tid" value={fastest ? formatTime(fastest.time!) : null} who={fastest} me={p.me} />
        <Record icon={<BoltIcon size={12} />} label="Flest point" value={top ? top.score.toLocaleString("da-DK") : null} who={top} me={p.me} showDifficulty />
      </div>
      <div className="relative flex justify-between px-5 py-2.5 bg-black/35 font-mono text-[9px] tracking-[0.12em] uppercase text-white/60">
        <span>Dig</span>
        <span className="tabular-nums">
          {mine?.time != null ? formatTime(mine.time) : "–:––"} · {myScore >= 0 ? myScore.toLocaleString("da-DK") : "–"} p
        </span>
      </div>
    </button>
  );
}

/** One record to beat: what, how much, and whose. */
function Record({ icon, label, value, who, me, showDifficulty }: { icon: ReactNode; label: string; value: string | null; who?: MenuRecord; me: Racer; showDifficulty?: boolean }) {
  const yours = who?.memberId === me.id;
  return (
    <div className="bg-black/40 px-4 py-3 min-w-0">
      <div className="flex items-center gap-1.5 font-mono text-[8.5px] tracking-[0.2em] uppercase text-white/55">
        <span className="text-[#ffc233]">{icon}</span>
        {label}
      </div>
      <div className={`mt-1 font-display text-[24px] font-extrabold italic tabular-nums leading-none tracking-[-0.02em] ${value ? "" : "text-white/30"}`}>
        {value ?? "—"}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 min-w-0 h-5">
        {who ? (
          <>
            <span className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 text-[10px] ring-1 ring-white/30">
              <Face racer={{ id: who.memberId, name: who.name, emoji: who.emoji }} />
            </span>
            <span className={`font-mono text-[9px] uppercase tracking-[0.08em] truncate ${yours ? "text-[#ffc233]" : "text-white/75"}`}>
              {yours ? "Din rekord" : who.name}
            </span>
            {showDifficulty && (
              <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.12em] text-white/45 flex-shrink-0">{DIFFICULTIES[who.difficulty as Difficulty] ?? who.difficulty}</span>
            )}
          </>
        ) : (
          <span className="font-mono text-[9px] uppercase tracking-[0.08em] text-white/40">Sæt den første</span>
        )}
      </div>
    </div>
  );
}

/** The track from above, as a line of light: a soft glow, the road, a centre line, and the start. */
function TrackLine({ track, glow }: { track: Track; glow: string }) {
  const { d, start, box } = useMemo(() => {
    const { samples } = track;
    let [x0, x1, z0, z1] = [Infinity, -Infinity, Infinity, -Infinity];
    for (const s of samples) {
      x0 = Math.min(x0, s.x);
      x1 = Math.max(x1, s.x);
      z0 = Math.min(z0, s.z);
      z1 = Math.max(z1, s.z);
    }
    const pts = samples.filter((_, i) => i % 5 === 0).map((s) => `${(s.x - x0).toFixed(1)},${(s.z - z0).toFixed(1)}`);
    const s0 = samples[0];
    return {
      d: `M${pts.join("L")}Z`,
      start: { x: s0.x - x0, z: s0.z - z0, a: (Math.atan2(s0.tz, s0.tx) * 180) / Math.PI },
      box: { w: x1 - x0, h: z1 - z0 },
    };
  }, [track]);
  const pad = Math.max(box.w, box.h) * 0.08;
  const unit = Math.max(box.w, box.h) / 100;
  return (
    <svg aria-hidden viewBox={`${-pad} ${-pad} ${box.w + 2 * pad} ${box.h + 2 * pad}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <path d={d} fill="none" stroke={glow} strokeOpacity={0.18} strokeWidth={unit * 9} strokeLinejoin="round" />
      <path d={d} fill="none" stroke="#000" strokeOpacity={0.45} strokeWidth={unit * 5.2} strokeLinejoin="round" transform={`translate(0 ${unit * 1.2})`} />
      <path d={d} fill="none" stroke="#fff" strokeWidth={unit * 4} strokeLinejoin="round" />
      <path d={d} fill="none" stroke={glow} strokeWidth={unit * 0.6} strokeDasharray={`${unit * 2} ${unit * 2}`} strokeLinejoin="round" />
      <g transform={`translate(${start.x} ${start.z}) rotate(${start.a})`}>
        <rect x={-unit * 1.2} y={-unit * 3.2} width={unit * 2.4} height={unit * 6.4} fill="#111" />
        {[0, 1, 2, 3].map((k) => (
          <rect key={k} x={k % 2 ? -unit * 1.2 : 0} y={-unit * 3.2 + k * unit * 1.6} width={unit * 1.2} height={unit * 1.6} fill="#fff" />
        ))}
        <path d={`M${unit * 7} 0 L${unit * 3} ${-unit * 2.4} L${unit * 3} ${unit * 2.4} Z`} fill="#ff3b30" />
      </g>
    </svg>
  );
}

/** A row of options with a pill that slides to the chosen one. */
function Segmented({ label, id, options, value, onChange }: { label: string; id: string; options: { key: string; label: string }[]; value: string; onChange: (key: string) => void }) {
  return (
    <div role="group" aria-label={label} className="flex items-center gap-3">
      <span className="w-[58px] flex-shrink-0 font-mono text-[8.5px] tracking-[0.2em] uppercase text-white/50">{label}</span>
      <div className="relative flex-1 grid rounded-full bg-white/[0.06] p-1" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
        {options.map((o) => {
          const on = o.key === value;
          return (
            <button
              key={o.key}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(o.key)}
              className={`relative min-h-[40px] rounded-full font-display text-[12.5px] font-bold uppercase tracking-tight transition-colors ${on ? "text-[#1a1206]" : "text-white/70"}`}
            >
              {on && (
                <m.span
                  layoutId={`seg-${id}`}
                  className="absolute inset-0 rounded-full"
                  style={{ background: GOLD, boxShadow: "0 1px 0 rgba(255,255,255,0.5) inset, 0 6px 16px -6px rgba(255,150,30,0.8)" }}
                  transition={{ type: "spring", stiffness: 500, damping: 38 }}
                />
              )}
              <span className="relative">{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Settings ─────────────────────────────────────────────────────────────────

const HELP_ICONS: Record<keyof Assists, { tint: string; path: string }> = {
  steer: { tint: "linear-gradient(135deg,#f97316,#c2410c)", path: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 3a6 6 0 0 1 5.7 4.2H6.3A6 6 0 0 1 12 6Zm-1.2 11.9A6 6 0 0 1 6.1 13h3.4l1.3 1.3v3.6Zm2.4 0v-3.6l1.3-1.3h3.4a6 6 0 0 1-4.7 4.9Z" },
  drift: { tint: "linear-gradient(135deg,#a855f7,#6b21a8)", path: "M4 17c3-9 9-13 16-11l-2 3c-5-1-9 2-11 8Zm12 1 3-4 1 5Z" },
  item: { tint: "linear-gradient(135deg,#ef4444,#991b1b)", path: "M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z" },
};

function Settings(p: Props) {
  const noHelp = !helped(p.assists);
  return (
    <m.div {...enter} className="relative flex-1 flex flex-col min-h-0">
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#0b0e13]/92 to-[#05070a]" />
      <div className="relative flex flex-col flex-1 min-h-0">
        <Header over="Garagen" title="Indstillinger" onBack={() => p.setPage("home")} />
        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-4 pt-2 flex flex-col gap-5">
          <Group title="Hjælp">
            {!noHelp && (
              <div className="flex items-center gap-3 px-4 py-3.5" style={{ background: "linear-gradient(90deg, rgba(255,194,51,0.16), transparent)" }}>
                <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-[#ffc233] text-[#0b0e13]">
                  <span className="font-display font-extrabold text-[18px]">!</span>
                </span>
                <span className="leading-snug">
                  <span className="block font-display text-[15px] font-bold uppercase tracking-tight">Du kører med hjælp</span>
                  <span className="block font-mono text-[9.5px] text-white/60">Tid og point tæller ikke på ranglisten.</span>
                </span>
              </div>
            )}
            {(Object.keys(ASSIST_LABELS) as (keyof Assists)[]).map((key) => (
              <Switch
                key={key}
                label={ASSIST_LABELS[key].label}
                text={ASSIST_LABELS[key].text}
                on={p.assists[key]}
                onToggle={() => p.toggleAssist(key)}
                icon={
                  <span className="w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: HELP_ICONS[key].tint }}>
                    <svg aria-hidden viewBox="0 0 24 24" width={20} height={20}>
                      <path d={HELP_ICONS[key].path} fill="#fff" fillRule="evenodd" />
                    </svg>
                  </span>
                }
              />
            ))}
          </Group>
          <Group title="Styring">
            <div className="px-4 py-3.5">
              <Segmented
                label="Følsomhed"
                id="steer"
                options={(Object.keys(STEER_LABELS) as (keyof typeof STEER_LABELS)[]).map((k) => ({ key: k, label: STEER_LABELS[k] }))}
                value={p.sensitivity}
                onChange={(k) => p.setSensitivity(k as keyof typeof STEER_LABELS)}
              />
            </div>
          </Group>
          <Group title="Lyd">
            <Switch
              label="Lyd og musik"
              text={p.mute ? "Slået fra" : "Motor, musik og våben"}
              on={!p.mute}
              onToggle={p.toggleMute}
              icon={
                <span className="w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: "linear-gradient(135deg,#22c55e,#15803d)" }}>
                  <SpeakerIcon muted={p.mute} size={20} />
                </span>
              }
            />
          </Group>
        </div>
      </div>
    </m.div>
  );
}

function Group({ title, foot, children }: { title: string; foot?: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="px-4 pb-2 font-mono text-[9.5px] tracking-[0.28em] uppercase text-[#ffc233]">{title}</h3>
      <div className={`rounded-[24px] overflow-hidden divide-y divide-white/[0.07] ${GLASS}`}>{children}</div>
      {foot && <p className="px-4 pt-2 font-mono text-[9px] leading-relaxed text-white/45">{foot}</p>}
    </section>
  );
}

function Switch({ label, text, on, onToggle, icon }: { label: string; text: string; on: boolean; onToggle: () => void; icon?: ReactNode }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={onToggle} className="w-full flex items-center gap-3 min-h-[60px] px-4 py-2.5 text-left active:bg-white/[0.05] transition-colors">
      {icon}
      <span className="flex-1 min-w-0 leading-tight">
        <span className="block font-display text-[15px] font-bold uppercase tracking-tight">{label}</span>
        <span className="block mt-0.5 font-mono text-[9px] leading-snug text-white/55">{text}</span>
      </span>
      <span
        className="relative w-[50px] h-[30px] rounded-full flex-shrink-0 transition-colors duration-200 p-[3px] flex"
        style={{ background: on ? GOLD : "rgba(255,255,255,0.14)", justifyContent: on ? "flex-end" : "flex-start" }}
      >
        <m.span layout transition={{ type: "spring", stiffness: 600, damping: 34 }} className="w-6 h-6 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.35)]" />
      </span>
    </button>
  );
}
